import { useEffect, useId, useRef, useState } from 'react';
import type { FullUser } from '../pages/ProfilePage';
import { API_BASE_URL } from '../constants';
import './AdminPatientSearch.css';

const formatOptionalDate = (value?: string | number | null) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatPatientDisplayName = (patient?: FullUser) => {
  if (!patient) {
    return '';
  }

  const title = String(patient.title ?? '').trim();
  const firstName = String(patient.firstName ?? '').trim();
  const familyName = String(patient.familyName ?? '').trim();

  return [title, firstName, familyName].filter(Boolean).join(' ').trim();
};

const formatPatientOptionLabel = (patient?: FullUser) => {
  if (!patient) {
    return '';
  }

  const displayName = formatPatientDisplayName(patient);
  const dateOfBirth = formatOptionalDate(patient.dateOfBirth);

  return dateOfBirth ? `${displayName} (${dateOfBirth})` : displayName;
};

const resolvePatientId = (patients: unknown): string => {
  if (!Array.isArray(patients) || patients.length === 0) return '';
  const first = patients[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') {
    const candidate = (first as { patientId?: unknown; id?: unknown }).patientId ?? (first as { id?: unknown }).id;
    return typeof candidate === 'string' ? candidate : '';
  }
  return '';
};

type AdminPatientSearchProps = {
  id: string;
  selectedPatient: FullUser | null;
  onSelect: (patient: FullUser) => void;
  onReset: () => void;
};

const normalizeName = (value: string) => value.trim().normalize('NFC').toLocaleLowerCase('de-CH').replace(/\s+/g, ' ');

/** The current API only accepts familyName. Suffixes also cover compound surnames. */
const surnameQueries = (value: string) => {
  const parts = value.trim().split(/\s+/);
  return parts.map((_, index) => parts.slice(index).join(' '));
};

/** Shared inline search; every request is constrained to a nonempty surname. */
export function AdminPatientSearch({ id, selectedPatient, onSelect, onReset }: AdminPatientSearchProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FullUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [revision, setRevision] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef('');
  const requestRef = useRef(0);
  const searchAbortRef = useRef<AbortController | null>(null);
  const selectionAbortRef = useRef<AbortController | null>(null);

  const invalidateRequests = () => {
    requestRef.current += 1;
    searchAbortRef.current?.abort();
    selectionAbortRef.current?.abort();
    setResults([]);
    setError('');
    setLoading(false);
    setSelecting(false);
    setActiveIndex(-1);
  };

  const clear = () => {
    // Update synchronously before focusing: focus and late responses must not reopen the list.
    queryRef.current = '';
    invalidateRequests();
    setQuery('');
    setOpen(false);
    localStorage.removeItem('selectedPatientId');
    onReset();
    inputRef.current?.focus();
  };

  const selectPatient = async (patient: FullUser) => {
    selectionAbortRef.current?.abort();
    const controller = new AbortController();
    selectionAbortRef.current = controller;
    setSelecting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Missing authorization.');
      const response = await fetch(`${API_BASE_URL}/patients/user/${patient.id}`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Bad patient lookup response: ${response.status}`);
      const patientId = resolvePatientId(await response.json());
      if (controller.signal.aborted) return;
      if (!patientId) throw new Error('No accessible patient ID returned.');
      queryRef.current = '';
      invalidateRequests();
      setQuery('');
      setOpen(false);
      localStorage.setItem('selectedPatientId', patientId);
      onSelect(patient);
    } catch (lookupError) {
      if (!controller.signal.aborted) {
        setError('Patient konnte nicht übernommen werden. Bitte erneut versuchen.');
      }
    } finally {
      if (!controller.signal.aborted) setSelecting(false);
    }
  };

  useEffect(() => {
    const nameQuery = query.trim();
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setResults([]);
    setError('');
    setActiveIndex(-1);
    if (selectedPatient || !nameQuery) {
      setLoading(false);
      setOpen(false);
      return () => controller.abort();
    }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Missing authorization.');
        // Never emulate a first-name search by downloading unfiltered patient lists.
        // Each possible surname suffix is searched through the existing authenticated API.
        const responses = await Promise.all(surnameQueries(nameQuery).map(async familyName => {
          const response = await fetch(`${API_BASE_URL}/user/search-full`, {
            method: 'POST',
            signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ familyName }),
          });
          if (!response.ok) throw new Error(`Bad user search response: ${response.status}`);
          const body = await response.json();
          return (Array.isArray(body) ? body : body ? [body] : []) as FullUser[];
        }));
        if (controller.signal.aborted || requestId !== requestRef.current) return;
        const users = Array.from(new Map(responses.flat().map(user => [user.id, user])).values());
        const prefix = normalizeName(nameQuery);
        const matches = users.filter(user =>
          normalizeName(String(user.familyName ?? '')).startsWith(prefix) ||
          normalizeName(`${user.firstName ?? ''} ${user.familyName ?? ''}`).startsWith(prefix)
        );
        setResults(matches);
        setActiveIndex(matches.length ? 0 : -1);
      } catch (searchError) {
        if (!controller.signal.aborted && requestId === requestRef.current) {
          setError('Patienten konnten nicht gesucht werden. Bitte erneut versuchen.');
        }
      } finally {
        if (!controller.signal.aborted && requestId === requestRef.current) setLoading(false);
      }
    }, 150);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, selectedPatient, revision]);

  useEffect(() => {
    if (open) suggestionsRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);
  useEffect(() => () => selectionAbortRef.current?.abort(), []);

  return (
    <div className="admin-patient-search">
      <svg className="admin-patient-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" strokeLinecap="round" />
      </svg>
      <input
        ref={inputRef}
        id={id}
        className="admin-patient-search__input"
        type="text"
        role="combobox"
        aria-label="Nachname suchen"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        autoComplete="off"
        placeholder="Patient suchen"
        value={selectedPatient ? formatPatientDisplayName(selectedPatient) : query}
        onChange={event => {
          invalidateRequests();
          // Editing a selected name invalidates its ID until another real patient is selected.
          if (selectedPatient || localStorage.getItem('selectedPatientId')) {
            localStorage.removeItem('selectedPatientId');
            onReset();
          }
          queryRef.current = event.target.value;
          setQuery(event.target.value);
          setOpen(Boolean(event.target.value.trim()));
        }}
        onFocus={() => { if (queryRef.current.trim() && !selectedPatient) setOpen(true); }}
        onBlur={() => setOpen(false)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(Boolean(queryRef.current.trim()));
            if (results.length) setActiveIndex(index => event.key === 'ArrowDown'
              ? (index + 1) % results.length : (index <= 0 ? results.length - 1 : index - 1));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            const patient = open ? results[activeIndex] : undefined;
            if (patient && !selecting) void selectPatient(patient);
            else if (!loading && !selecting && queryRef.current.trim()) {
              setOpen(true);
              setRevision(value => value + 1);
            }
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
          }
        }}
      />
      <button
        type="button"
        className="admin-patient-search__clear"
        aria-label="Patientensuche zurücksetzen"
        onMouseDown={event => event.preventDefault()}
        onClick={clear}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="m5 5 14 14M19 5 5 19" strokeLinecap="round" />
        </svg>
      </button>
      {open ? (
        <div ref={suggestionsRef} className="admin-patient-search__suggestions">
          <div id={listId} role="listbox" aria-label="Patienten" aria-busy={loading || selecting}>
            {results.map((patient, index) => (
              <div
                id={`${listId}-${index}`}
                key={patient.id}
                role="option"
                aria-selected={activeIndex === index}
                aria-disabled={selecting}
                className="admin-patient-search__option"
                onMouseDown={event => event.preventDefault()}
                onClick={() => { if (!selecting) void selectPatient(patient); }}
              >
                {formatPatientOptionLabel(patient)}
              </div>
            ))}
          </div>
          <div role="status" aria-live="polite" className="admin-patient-search__status">
            {selecting ? 'Patient wird übernommen…' : loading ? 'Patienten werden gesucht…' :
              !error && results.length === 0 ? 'Keine Patienten gefunden' : ''}
          </div>
          {error ? <p role="alert" className="admin-patient-search__error">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
